/**
 * The frame-remap abstraction (ADR-005 §3): ONE law covering proper and
 * improper frame relationships between source and target bone-local frames.
 *
 * Proper (rotation-related) frames: the Gen-2 conjugation sandwich
 * R⁻¹·δ·R (quatConjugateBy). Improper (reflection-related) frames: the
 * accepted Sol result — axis-angle (u,θ) → (det(F)·Fᵀu, θ), i.e. for a unit
 * quaternion δ=(v,w) and improper orthogonal F: δ' = (−Fᵀv, w).
 * See docs/research/proposals/reflection-aware-conjugation/ (ACCEPTED
 * 2026-07-11) — the reference derivation and 12 adopted golden vectors.
 *
 * Depends on: math/quat, math/mat3, ir/errors.
 */
import { quatConjugateBy, quatInvertUnit, quatMultiply, type Quat } from '../math/quat.js';
import { mat3Determinant, mat3OrthogonalityError, mat3TransposeMulVec, type Mat3 } from '../math/mat3.js';
import { KernelError } from '../ir/errors.js';

/** A validated source-local → target-local frame relationship for one bone. */
export type FrameMap =
  | { readonly kind: 'proper'; readonly rotation: Quat }
  | { readonly kind: 'improper'; readonly matrix: Mat3 };

export class InvalidFrameMapError extends KernelError {}

const ORTHO_TOL = 1e-6;

/** The basic Gen-2 reorientation: R = srcBind⁻¹ · tgtBind (local bind quats). */
export function properFrameMap(sourceBind: Quat, targetBind: Quat): FrameMap {
  return { kind: 'proper', rotation: quatMultiply(quatInvertUnit(sourceBind), targetBind) };
}

/**
 * An improper frame map from a declared reflection relationship (mirror plane
 * + parity convention — the metadata Sol's impossibility result requires).
 * Validated: orthogonal with det ≈ −1, loudly.
 */
export function improperFrameMap(matrix: Mat3): FrameMap {
  const ortho = mat3OrthogonalityError(matrix);
  if (ortho > ORTHO_TOL) {
    throw new InvalidFrameMapError(
      `improper frame map is not orthogonal (‖MᵀM−I‖∞ = ${ortho.toExponential(2)})`,
    );
  }
  const det = mat3Determinant(matrix);
  if (Math.abs(det + 1) > ORTHO_TOL) {
    throw new InvalidFrameMapError(
      `improper frame map must have det ≈ −1, got ${det.toFixed(6)} — ` +
        `a proper rotation belongs in properFrameMap (quaternion form)`,
    );
  }
  return { kind: 'improper', matrix };
}

export const IDENTITY_FRAME_MAP: FrameMap = Object.freeze({
  kind: 'proper',
  rotation: Object.freeze({ x: 0, y: 0, z: 0, w: 1 }),
}) as FrameMap;

/**
 * Remap a delta rotation from the source bone's local frame into the target
 * bone's local frame. Rotation ANGLE is preserved in both branches; the
 * improper branch applies the pseudovector sign law.
 */
export function remapDelta(delta: Quat, frameMap: FrameMap): Quat {
  if (frameMap.kind === 'proper') {
    return quatConjugateBy(delta, frameMap.rotation);
  }
  // Sol's law, quaternion fast path: δ' = (det(F)·Fᵀv, w) with det(F) = −1.
  const v = mat3TransposeMulVec(frameMap.matrix, delta);
  return { x: -v.x, y: -v.y, z: -v.z, w: delta.w };
}
